import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Building2, Loader2, X, AlertTriangle } from 'lucide-react';
import CompanyExportCard from './CompanyExportCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = { fontFamily: 'IBM Plex Sans, sans-serif' };

// App Store 5.1.1(v) tier B: the company owner schedules deletion of the whole tenant (30-day grace).
export const DeleteCompanyCard = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => axios.get(`${API}/company/deletion-status`, { withCredentials: true }).then((r) => setStatus(r.data)).catch(() => setStatus({ scheduled: false }));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/company/delete`, { password, confirm, company_name: companyName }, { withCredentials: true });
      toast.success(`Deletion scheduled for ${new Date(data.purge_at).toLocaleDateString()}`);
      setStep(0); setPassword(''); setConfirm(''); setCompanyName('');
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not schedule deletion.');
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/company/delete/cancel`, {}, { withCredentials: true });
      toast.success('Company deletion cancelled');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel');
    } finally { setBusy(false); }
  };

  if (user?.role !== 'company_admin') return null;

  return (
    <div className="bg-white border border-red-300 rounded-md p-6 mt-6" data-testid="delete-company-card">
      <h2 className="text-lg font-bold tracking-tight text-red-700 mb-2 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
        <Building2 className="w-5 h-5" /> Delete Company Workspace
      </h2>
      <p className="text-sm text-slate-600 mb-4" style={FONT}>
        Permanently deletes your company's entire CivilX workspace — every user account, load, driver record, document,
        payroll run and invoice. Deletion takes effect after a <strong>{status?.grace_days ?? 30}-day grace period</strong>; any company
        administrator can cancel it before then. Records CivilX itself must keep for its own accounting (billing transactions,
        security logs) are retained without your company's operational data. Export what you need first.
      </p>
      <CompanyExportCard compact />

      {status?.scheduled ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-red-50 border border-red-200 rounded-md p-4" style={FONT} data-testid="delete-company-scheduled">
          <AlertTriangle className="w-5 h-5 text-red-700 shrink-0" />
          <div className="flex-1 text-sm text-red-900">
            Scheduled by <strong>{status.requested_by_name}</strong>. Everything will be permanently erased on{' '}
            <strong data-testid="delete-company-purge-date">{new Date(status.purge_at).toLocaleDateString()}</strong>.
          </div>
          <button type="button" onClick={cancel} disabled={busy} data-testid="delete-company-cancel-schedule"
            className="px-4 py-2 rounded-md bg-white border border-red-300 text-red-700 font-semibold text-sm hover:bg-red-100 disabled:opacity-50">
            Cancel deletion
          </button>
        </div>
      ) : (
        <button type="button" data-testid="delete-company-button" onClick={() => setStep(1)}
          className="px-4 py-2 rounded-md bg-red-600 text-white font-semibold text-sm hover:bg-red-700" style={FONT}>
          Delete company workspace…
        </button>
      )}

      {step > 0 && (
        <div className="fixed inset-0 z-[70] bg-slate-900/60 flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true">
          <form onSubmit={submit} className="bg-white w-full max-w-md rounded-lg shadow-xl p-6 space-y-4" style={FONT} data-testid="delete-company-modal">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {step === 1 ? 'Step 1 of 2 — Confirm the company' : 'Step 2 of 2 — Final confirmation'}
              </h3>
              <button type="button" onClick={() => setStep(0)} className="p-1 text-slate-500" aria-label="Close" data-testid="delete-company-close"><X className="w-5 h-5" /></button>
            </div>
            {step === 1 && (
              <>
                <p className="text-sm text-slate-600">Type your company name exactly as it appears in CivilX to continue.</p>
                <input data-testid="delete-company-name" type="text" required value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Company name" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-red-500" />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setStep(0)} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100">Cancel</button>
                  <button type="button" disabled={!companyName.trim()} onClick={() => setStep(2)} data-testid="delete-company-next"
                    className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">Continue</button>
                </div>
              </>
            )}
            {step === 2 && (
              <>
                <p className="text-sm text-slate-600">This schedules irreversible deletion of <strong>{companyName}</strong> in {status?.grace_days ?? 30} days. All administrators will be notified.</p>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600">Your password
                  <input data-testid="delete-company-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm tracking-normal focus:outline-none focus:border-red-500" />
                </label>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600">Type <span className="text-red-700">DELETE</span>
                  <input data-testid="delete-company-confirm" type="text" required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoCapitalize="characters" autoComplete="off"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm tracking-normal focus:outline-none focus:border-red-500" />
                </label>
                {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="delete-company-error">{error}</p>}
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setStep(1)} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100">Back</button>
                  <button type="submit" disabled={busy || confirm.trim().toUpperCase() !== 'DELETE' || !password} data-testid="delete-company-submit"
                    className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />} Schedule deletion
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default DeleteCompanyCard;
